import { Badge } from "@design";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { ExtensionResponse } from "@/api";
import { CopyValue } from "@/components/CopyValue";
import { DefinitionList, PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { formatList } from "@/i18n/format";
import { configFields } from "./ConfigurationSection";
import { credentialLabel } from "./setupStatus";

const CAPABILITIES = ["context", "action", "artifact"] as const;
type Capability = (typeof CAPABILITIES)[number];
const isCapability = (c: string): c is Capability => (CAPABILITIES as readonly string[]).includes(c);

const LIST_CLASS = "flex flex-col divide-y divide-border-subtle border-y border-border-subtle";

export function ToolsSection({ extension }: Readonly<{ extension: ExtensionResponse }>) {
  const { t } = useTranslation("adminIntegrations");
  const tools = extension.tools ?? [];
  return (
    <PageSection title={t("detail.tools")} description={t("detail.toolsBody")}>
      {tools.length === 0 ? (
        <Text variant="body-sm" tone="tertiary">
          {t("detail.noTools")}
        </Text>
      ) : (
        <ul aria-label={t("detail.tools")} className={LIST_CLASS}>
          {tools.map((tool) => (
            <li key={tool.uuid} className="flex flex-col gap-1 py-3">
              <span className="flex flex-wrap items-center gap-2">
                <Text as="span" variant="body-sm">
                  {tool.name}
                </Text>
                {(tool.capabilities ?? []).filter(isCapability).map((c) => (
                  <Badge key={c} size="compact" variant="neutral">
                    {t(`detail.capabilities.${c}`)}
                  </Badge>
                ))}
                {tool.escalation === "required" && (
                  <Badge size="compact" variant="warning">
                    {t("detail.asksFirst")}
                  </Badge>
                )}
                {!tool.isEnabled && (
                  <Badge size="compact" variant="neutral">
                    {t("detail.toolOff")}
                  </Badge>
                )}
              </span>
              {tool.description && (
                <Text variant="caption" tone="tertiary">
                  {tool.description}
                </Text>
              )}
            </li>
          ))}
        </ul>
      )}
    </PageSection>
  );
}

export function SkillsSection({ extension }: Readonly<{ extension: ExtensionResponse }>) {
  const { t } = useTranslation("adminIntegrations");
  const skills = extension.skills ?? [];
  return (
    <PageSection title={t("detail.skills")} description={t("detail.skillsBody")}>
      {skills.length === 0 ? (
        <Text variant="body-sm" tone="tertiary">
          {t("detail.noSkills")}
        </Text>
      ) : (
        <ul aria-label={t("detail.skills")} className={LIST_CLASS}>
          {skills.map((skill) => (
            <li key={skill.uuid} className="flex flex-col gap-1 py-3">
              <Link
                to={`/build/skills/${skill.uuid}`}
                className="btn-focus-ring self-start rounded text-content-primary underline underline-offset-2"
              >
                <Text as="span" variant="body-sm">
                  {skill.name}
                </Text>
              </Link>
              <Text variant="caption" tone="tertiary">
                {skill.description}
              </Text>
            </li>
          ))}
        </ul>
      )}
    </PageSection>
  );
}

export function CredentialsSection({ extension }: Readonly<{ extension: ExtensionResponse }>) {
  const { t } = useTranslation("adminIntegrations");
  // Only the integration's own fields; values left by older features aren't configuration.
  const defined = new Set(
    configFields(extension)
      .filter((f) => !f.extra)
      .map((f) => f.key),
  );
  const credentials = (extension.credentials ?? []).filter((c) => defined.has(c.key));
  const status = (c: (typeof credentials)[number]) => {
    if (c.hasDbValue) return <Badge variant="success">{t("detail.credential.saved")}</Badge>;
    if (c.isConfigured) return <Badge variant="success">{t("detail.credential.env", { name: c.envVarName })}</Badge>;
    return <Badge variant="warning">{t("detail.credential.missing")}</Badge>;
  };
  return (
    <PageSection title={t("detail.credentials")} description={t("detail.credentialsBody")}>
      {credentials.length === 0 ? (
        <Text variant="body-sm" tone="tertiary">
          {t("detail.noCredentials")}
        </Text>
      ) : (
        <DefinitionList
          items={credentials.map((c) => ({ label: credentialLabel(extension, c.key), value: status(c) }))}
        />
      )}
    </PageSection>
  );
}

/** For per-user sign-in integrations: the redirect URI to register and the scopes asked for. */
export function PerUserSignIn({ extension }: Readonly<{ extension: ExtensionResponse }>) {
  const { t } = useTranslation("adminIntegrations");
  const oauth = extension.userOauthSetup;
  if (!oauth) return null;
  return (
    <div className="flex flex-col gap-3">
      <Text variant="h3">{t("detail.perUser")}</Text>
      <Text variant="body-sm" tone="secondary">
        {t("detail.perUserBody", { provider: extension.name })}
      </Text>
      <div className="max-w-xl">
        <CopyValue value={oauth.redirectUri} label={t("detail.redirectUri")} />
      </div>
      {(oauth.scopes ?? []).length > 0 && (
        <DefinitionList items={[{ label: t("detail.scopes"), value: (oauth.scopes ?? []).join(" ") }]} />
      )}
    </div>
  );
}

export function AccessSection({ extension }: Readonly<{ extension: ExtensionResponse }>) {
  const { t } = useTranslation("adminIntegrations");
  const teams = extension.teamAccess ?? [];
  return (
    <PageSection title={t("detail.access")}>
      <Text variant="body-sm" tone="secondary">
        {teams.length === 0 ? t("detail.everyone") : formatList(teams.map((team) => team.teamName))}
      </Text>
      <PerUserSignIn extension={extension} />
    </PageSection>
  );
}
